import { useTranslations } from 'use-intl'
import { Image } from '@unpic/react'

interface Highlight {
  tag: string
  image: string
  content: string
}

interface ShopHighlightsProps {
  highlights: Highlight[] | null
}

export function ShopHighlights({ highlights }: ShopHighlightsProps) {
  const t = useTranslations('shopDetail')

  if (!highlights || highlights.length === 0) return null

  const firstHighlight = highlights[0]
  const restHighlights = highlights.slice(1)

  return (
    <section className="w-full pb-8">
      <div className="flex items-center gap-4 mb-12">
        <div className="h-[1px] w-12 bg-primary"></div>
        <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-primary">
          {t('highlights')}
        </span>
      </div>

      {/* Editorial layout for the first highlight (Stitch Design) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center mb-16">
        <div className="lg:col-span-7 relative">
          <div className="rounded-xl overflow-hidden aspect-[4/5] shadow-2xl">
            {firstHighlight.image && (
              <Image 
                src={firstHighlight.image} 
                layout="fullWidth" 
                alt={firstHighlight.tag} 
                className="w-full h-full object-cover" 
              />
            )}
          </div>
          {/* Overlapping Card */}
          <div className="absolute -bottom-6 -right-2 lg:-bottom-10 lg:-right-12 bg-surface-container-lowest p-6 lg:p-10 rounded-xl shadow-[0_30px_60px_rgba(0,0,0,0.08)] max-w-[85%] lg:max-w-sm border border-outline-variant/10">
            <h3 className="text-2xl lg:text-3xl font-black text-on-surface mb-3 lg:mb-4 leading-tight">
              {firstHighlight.tag}
            </h3>
            <p className="text-on-surface-variant leading-relaxed text-sm">
              {firstHighlight.content}
            </p>
          </div>
        </div>
        {/* We can leave the right side empty or put something here if we adapt it. In Stitch, the right side had the main title, but we already have a Header component. */}
      </div>

      {/* Compact cards for the rest */}
      {restHighlights.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {restHighlights.map((highlight, idx) => (
            <div key={idx} className="bg-surface-container-low rounded-xl overflow-hidden shadow-sm flex flex-col">
              {highlight.image && (
                <div className="aspect-[3/2] w-full">
                  <Image 
                    src={highlight.image} 
                    layout="fullWidth" 
                    alt={highlight.tag} 
                    className="w-full h-full object-cover" 
                  />
                </div>
              )}
              <div className="p-6">
                <h4 className="font-bold text-lg mb-2 text-on-surface">{highlight.tag}</h4>
                <p className="text-sm text-on-surface-variant leading-relaxed">{highlight.content}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
